import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, brl, productImage, uploadUrl } from "../lib/api";
import { useCart, useAuth } from "../lib/store";
import { toast } from "sonner";
import { ShieldCheck, Truck, Store, Tag, X, User, LogIn } from "lucide-react";

export default function Checkout() {
  const nav = useNavigate();
  const { items, total, clear } = useCart();
  const { user } = useAuth();
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    address: "", notes: "", delivery_method: "retirada", coupon_code: "",
  });
  const [couponInfo, setCouponInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  // Pré-preenche com dados do cliente logado
  useEffect(() => {
    if (user && user.role === "customer") {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        customer_email: f.customer_email || user.email || "",
        customer_phone: f.customer_phone || user.phone || "",
      }));
      // Auto-aplica cupom Instagram se cliente segue e ainda não aplicou nenhum
      if (user.instagram_followed) {
        (async () => {
          try {
            const { data } = await api.get("/coupons/validate", { params: { code: "EUSIGO10", total } });
            setCouponInfo((prev) => prev || data);
            setForm((f) => ({ ...f, coupon_code: f.coupon_code || "EUSIGO10" }));
          } catch { /* silent */ }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (items.length === 0) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <div className="eyebrow">Checkout</div>
        <h1 className="mt-3 font-display text-4xl text-[#0D1B2A]">Seu carrinho está vazio</h1>
        <Link to="/catalogo" className="mt-6 inline-block btn-gold">Explorar catálogo</Link>
      </div>
    );
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const finalTotal = Math.max(0, total - (couponInfo?.discount || 0));

  const applyCoupon = async () => {
    if (!form.coupon_code) return;
    setApplying(true);
    try {
      const { data } = await api.get("/coupons/validate", { params: { code: form.coupon_code.trim(), total } });
      setCouponInfo(data);
      toast.success(`Cupom ${data.code} aplicado (-${brl(data.discount)})`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cupom inválido");
      setCouponInfo(null);
    } finally { setApplying(false); }
  };
  const removeCoupon = () => { setCouponInfo(null); set("coupon_code", ""); };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        coupon_code: couponInfo?.code || "",
        items: items.map((i) => ({
          product_id: i.product_id, quantity: i.quantity,
          customization: i.customization || {}, photo_id: i.photo_id || null,
        })),
      };
      const { data } = await api.post("/checkout", payload);
      clear();
      if (data.init_point) {
        window.location.href = data.init_point;
      } else {
        toast.success("Pedido registrado! Abrindo WhatsApp para conclusão.");
        nav(`/checkout/pendente?order=${data.order_id}`);
        setTimeout(() => window.open(data.admin_wa_link, "_blank"), 800);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no checkout");
    } finally { setLoading(false); }
  };

  return (
    <div className="mx-auto max-w-6xl px-6 lg:px-8 py-14 grid lg:grid-cols-5 gap-10" data-testid="checkout-root">
      <form onSubmit={submit} className="lg:col-span-3 space-y-6">
        <div>
          <div className="eyebrow">Finalizar compra</div>
          <h1 className="mt-2 font-display text-4xl text-[#0D1B2A]">
            {user && user.role === "customer" ? "Confirmar dados" : "Seus dados"}
          </h1>
        </div>

        {user && user.role === "customer" ? (
          <div data-testid="checkout-logged-card" className="bg-[#F7F2E7] border border-[#D4AF37]/40 rounded-2xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-[#D4AF37]/40">
              <User size={18} className="text-[#0D1B2A]" />
            </div>
            <div className="flex-1 text-sm">
              <div className="font-medium text-[#0D1B2A]">Comprando como <b>{user.name}</b></div>
              <div className="text-[#6B7280] text-xs">{user.email} · {user.phone || "Sem telefone cadastrado"}</div>
            </div>
            <Link to="/minha-conta" className="text-xs text-[#D4AF37] shrink-0">Editar</Link>
          </div>
        ) : (
          <div className="bg-white border border-[#0D1B2A]/10 rounded-2xl p-4 flex items-center gap-3">
            <LogIn size={18} className="text-[#D4AF37]" />
            <div className="flex-1 text-sm">
              <div className="font-medium text-[#0D1B2A]">Já tem conta?</div>
              <div className="text-[#6B7280] text-xs">Entre para preencher automaticamente e ganhar cupom</div>
            </div>
            <Link to="/entrar?next=/checkout" className="text-xs text-[#D4AF37] shrink-0 font-medium">Entrar →</Link>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <div><label className="text-sm text-[#3A4A5F]">Nome completo</label>
            <input data-testid="checkout-name" required className="input-luxe mt-1" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} /></div>
          <div><label className="text-sm text-[#3A4A5F]">WhatsApp</label>
            <input data-testid="checkout-phone" required className="input-luxe mt-1" value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></div>
          <div className="sm:col-span-2"><label className="text-sm text-[#3A4A5F]">E-mail</label>
            <input data-testid="checkout-email" required type="email" className="input-luxe mt-1" value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></div>
        </div>

        <div>
          <label className="eyebrow">Entrega</label>
          <div className="mt-2 grid sm:grid-cols-2 gap-3">
            {[
              { k: "retirada", icon: Store, t: "Retirada no local", s: "Combinar por WhatsApp" },
              { k: "envio", icon: Truck, t: "Envio para casa", s: "Frete calculado depois" },
            ].map(({ k, icon: I, t, s }) => (
              <label key={k} className={`flex gap-3 p-4 rounded-2xl border cursor-pointer ${form.delivery_method === k ? "border-[#D4AF37] bg-[#F7F2E7]" : "border-[#0D1B2A]/10 bg-white"}`}>
                <input data-testid={`delivery-${k}`} type="radio" name="d" checked={form.delivery_method === k} onChange={() => set("delivery_method", k)} className="mt-1" />
                <div>
                  <div className="flex items-center gap-2 font-medium text-[#0D1B2A]"><I size={16} /> {t}</div>
                  <div className="text-xs text-[#6B7280]">{s}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {form.delivery_method === "envio" && (
          <div><label className="text-sm text-[#3A4A5F]">Endereço completo</label>
            <input data-testid="checkout-address" className="input-luxe mt-1" placeholder="Rua, número, bairro, cidade, CEP" value={form.address} onChange={(e) => set("address", e.target.value)} /></div>
        )}

        <div><label className="text-sm text-[#3A4A5F]">Observações</label>
          <textarea data-testid="checkout-notes" className="input-luxe mt-1" rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>

        <button data-testid="checkout-submit" disabled={loading} className="btn-gold w-full py-4 text-base">
          {loading ? "Processando…" : `Ir para pagamento · ${brl(finalTotal)}`}
        </button>
        <p className="text-xs text-center text-[#6B7280] flex items-center justify-center gap-2">
          <ShieldCheck size={14} className="text-[#D4AF37]" /> Pagamento seguro Mercado Pago (Pix, cartão, boleto)
        </p>
      </form>

      <aside className="lg:col-span-2 bg-white rounded-3xl p-6 border border-[#0D1B2A]/5 h-fit sticky top-24">
        <div className="font-display text-2xl text-[#0D1B2A]">Resumo</div>
        <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
          {items.map((it, i) => (
            <div key={i} className="flex gap-3 text-sm">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F7F2E7] shrink-0">
                {it.photo_id ? <img src={uploadUrl(it.photo_id)} className="w-full h-full object-cover" alt="" />
                             : <img src={productImage(it)} className="w-full h-full object-cover" alt={it.name} />}
              </div>
              <div className="flex-1">
                <div className="font-medium text-[#0D1B2A]">{it.name}</div>
                <div className="text-xs text-[#6B7280]">Qtd {it.quantity} · {brl(it.price)}</div>
              </div>
              <div className="font-display text-[#0D1B2A]">{brl(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>

        <div className="gold-rule my-4" />

        {!couponInfo ? (
          <div className="flex gap-2">
            <input data-testid="coupon-input" value={form.coupon_code} onChange={(e) => set("coupon_code", e.target.value.toUpperCase())} placeholder="Cupom" className="input-luxe uppercase tracking-widest text-sm" />
            <button type="button" data-testid="coupon-apply" onClick={applyCoupon} disabled={applying || !form.coupon_code} className="btn-outline-navy shrink-0"><Tag size={14} /></button>
          </div>
        ) : (
          <div className="bg-[#F7F2E7] border border-[#D4AF37]/40 rounded-xl p-3 flex items-center justify-between">
            <div>
              <div className="text-xs text-[#6B7280] uppercase tracking-widest">Cupom aplicado</div>
              <div className="font-display text-lg text-[#0D1B2A]">{couponInfo.code} <span className="text-[#16A34A] text-sm">- {brl(couponInfo.discount)}</span></div>
            </div>
            <button type="button" onClick={removeCoupon} data-testid="coupon-remove"><X size={16} className="text-[#6B7280]" /></button>
          </div>
        )}

        <div className="mt-4 space-y-1 text-sm">
          <div className="flex justify-between text-[#6B7280]"><span>Subtotal</span><span>{brl(total)}</span></div>
          {couponInfo && <div className="flex justify-between text-[#16A34A]"><span>Desconto</span><span>- {brl(couponInfo.discount)}</span></div>}
          <div className="flex justify-between font-display text-xl text-[#0D1B2A] mt-2 pt-2 border-t border-[#0D1B2A]/10">
            <span>Total</span><span data-testid="checkout-total">{brl(finalTotal)}</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
